import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import Loading from 'src/components/Loading/Loading';
import { CustomDropdown, FilterOptionType, FilterValueType } from 'src/components/RealityCheckForm/CustomDropdown';
import {
    ButtonWrapper,
    ButtonTag,
} from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/ComplianceRestriction/ComplianceRestriction.style';
import { useAppStateContext } from 'src/AppState/AppState';
import { SdkStaff, StaffAccountBasicDataType } from 'src/AppState/sdk/staff';
import { DateTime } from 'src_common/utils/time/time';
import { ModelsState } from 'src/AppState/Models/ModelsState';

const COMPLIANCE_RESTRICTION_OPTIONS_ARRAY: FilterOptionType<string>[] = [
    {
        value: '7',
        label: '7 days',
    },
    {
        value: '14',
        label: '14 days',
    },
    {
        value: '30',
        label: '30 days',
    },
];

class ComplianceRestrictionState {
    @observable public selectedPeriod: string | null = null;
    @observable public isLoading: boolean = false;
    @observable public isError: boolean = false;

    public constructor(
        private readonly sdkStaff: SdkStaff,
        private readonly customerId: number,
        private readonly models: ModelsState
    ) {
        this.selectedPeriod = COMPLIANCE_RESTRICTION_OPTIONS_ARRAY[0]?.value ?? null;
        makeAutoObservable(this);
    }

    @computed private get basicDataUserModel(): StaffAccountBasicDataType | null {
        return this.models.getUserModel(this.customerId).basicData.data;
    }

    @computed public get getComplianceRestriction(): Exclude<
        StaffAccountBasicDataType['complianceRestriction'],
        undefined
    > {
        return this.basicDataUserModel?.complianceRestriction ?? null;
    }

    @computed public get sourceReason(): String {
        const capitalizedReason =
            this.getComplianceRestriction === null
                ? 'none'
                : this.getComplianceRestriction.source
                      .split('-')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');

        return capitalizedReason;
    }

    @computed public get sourceDescription(): string | null {
        return this.getComplianceRestriction?.reason ?? null;
    }

    @action public onSetComplianceRestriction = async (): Promise<void> => {
        await this.submitComplianceRestriction(true);
    };

    @action public onRemoveComplianceRestriction = async (): Promise<void> => {
        await this.submitComplianceRestriction(false);
    };

    @action private submitComplianceRestriction = async (enabled: boolean): Promise<void> => {
        this.isLoading = true;
        this.isError = false;
        const timestamp = new Date();
        const pendingSuspensionTime = new Date();
        pendingSuspensionTime.setDate(pendingSuspensionTime.getDate() + parseInt(this.selectedPeriod ?? '0', 10));

        try {
            await this.sdkStaff.setComplianceRestriction({
                requestBody: {
                    accountId: this.customerId.toString(),
                    pendingSuspensionTime: pendingSuspensionTime.toISOString(),
                    source: 'backOffice',
                    enabled,
                    timestamp: timestamp.toISOString(),
                },
            });

            this.isLoading = false;
        } catch (e) {
            this.isLoading = false;
            this.isError = true;
        }
    };

    @action public onSubmitSrg = async (): Promise<void> => {
        this.isLoading = true;
        this.isError = false;
        const timestamp = new Date();

        try {
            await this.sdkStaff.setSrgStatus({
                requestBody: {
                    accountId: this.customerId.toString(),
                    source: 'backOffice',
                    srgStatus: 'requested',
                    timestamp: timestamp.toISOString(),
                },
            });

            this.isLoading = false;
        } catch (err) {
            this.isLoading = false;
            this.isError = true;
        }
    };

    public onUpdatePeriod = (changedValue: FilterValueType): void => {
        if (typeof changedValue === 'string') {
            this.selectedPeriod = changedValue;
        }
    };
}

interface ComplianceRestrictionType {
    customerId: number;
}

export const ComplianceRestriction = observer('ComplianceRestriction', ({ customerId }: ComplianceRestrictionType) => {
    const { sdkStaff, models } = useAppStateContext();
    const [state] = React.useState(() => new ComplianceRestrictionState(sdkStaff, customerId, models));

    return (
        <div className='row'>
            <div className='col-4'>
                <h2 className='header'>Compliance Restriction:</h2>
                <table>
                    <tbody>
                        <tr>
                            <th>Reason:</th>
                            <td data-test='compliance-restriction-reason'>{state.sourceReason}</td>
                        </tr>
                        {state.sourceDescription !== null && state.sourceDescription !== '' ? (
                            <tr>
                                <th>Description:</th>
                                <td data-test='compliance-restriction-reason'>{state.sourceDescription}</td>
                            </tr>
                        ) : null}
                        <tr>
                            <th>Triggered:</th>
                            <td data-test='compliance-restriction-triggered'>
                                {state.getComplianceRestriction === null
                                    ? 'none'
                                    : DateTime.from(state.getComplianceRestriction.eventTime)?.format(
                                          'DD/MM/YYYY HH:mm:ss'
                                      )}
                            </td>
                        </tr>
                        <tr>
                            <th>Expires:</th>
                            <td data-test='compliance-restriction-expires'>
                                {state.getComplianceRestriction === null
                                    ? 'none'
                                    : DateTime.from(state.getComplianceRestriction.pendingSuspensionTime)?.format(
                                          'DD/MM/YYYY HH:mm:ss'
                                      )}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className='reality-check-form'>
                    <CustomDropdown
                        optionsList={(): Array<FilterOptionType<string | null>> => COMPLIANCE_RESTRICTION_OPTIONS_ARRAY}
                        selectedValue={(): string | null => state.selectedPeriod}
                        onChangeCallback={state.onUpdatePeriod}
                        placeholder='Restriction period'
                        labelHeader='Restriction period'
                    />

                    {state.isError ? (
                        <div className='element'>
                            <div className='error-message'>
                                <div className='error-message-content'>Something went wrong</div>
                            </div>
                        </div>
                    ) : null}

                    <ButtonWrapper
                        isDisabled={false}
                        className='buttons'
                    >
                        <ButtonTag
                            disabled={false}
                            onClick={state.onSetComplianceRestriction}
                            className='btn'
                            type='submit'
                            data-test='set-compliance-restriction-button'
                        >
                            Set compliance restriction
                        </ButtonTag>
                        <ButtonTag
                            disabled={false}
                            onClick={state.onRemoveComplianceRestriction}
                            className='btn'
                            type='submit'
                            isDeleteButton={true}
                            data-test='remove-compliance-restriction-button'
                        >
                            Remove compliance restriction
                        </ButtonTag>
                    </ButtonWrapper>

                    <ButtonTag
                        disabled={false}
                        onClick={state.onSubmitSrg}
                        className='btn'
                        type='submit'
                        data-test='set-srg-button'
                    >
                        Set SRG
                    </ButtonTag>
                </div>
                <Loading
                    active={state.isLoading}
                    // label='Setting compliance restriction...'
                />
            </div>
        </div>
    );
});
